import { useRouter } from 'next/navigation'
import { type Dispatch, type SetStateAction, useCallback } from 'react'

import { useModal } from '@src/components/Modal/ModalProvider'
import { useRailItemContext } from '@src/components/Rail/RailItemContext'
import { useSierra } from '@src/components/Sierra/SierraProvider'
import { configPublic } from '@src/config.public'
import { useCart } from '@src/hooks/useCart'
import { type UseClickActionArgs } from '@src/hooks/useClickAction/useClickActionArgs'
import { useClickActionContext } from '@src/hooks/useClickAction/useClickActionContext'
import { useTrackAction } from '@src/hooks/useClickAction/useTrackAction'
import { useTrackNavigate } from '@src/hooks/useClickAction/useTrackNavigate'
import { useTrackOpenModal } from '@src/hooks/useClickAction/useTrackOpenModal'
import { useErrors } from '@src/hooks/useErrors'
import { useLocalizedRouter } from '@src/hooks/useLocalizedRouter'
import { usePDP } from '@src/hooks/usePDP'
import { useSession } from '@src/hooks/useSession'
import { useLocale } from '@src/i18n/LocaleProvider'
import { ServerActions } from '@src/services/server-actions.service'
import { isSelectionStateValid, useSKUSelectionStore } from '@src/stores/SKUSelectionStore'
import { BuilderActionErrorsGroup } from '@src/utils/constants.util'
import { log } from '@src/utils/log.util'
import { getShopifyBaseUrl } from '@src/utils/url.util'

interface UseOnClickArgs {
  setVideoOpen: Dispatch<SetStateAction<boolean>>
  setIsLoading: Dispatch<SetStateAction<boolean>>
  setIsLoginModalOpen: Dispatch<SetStateAction<boolean>>
  shouldSkip?: boolean
  useClickActionArgs: UseClickActionArgs
}

export const useOnClick = (args: UseOnClickArgs) => {
  const { setVideoOpen, shouldSkip, useClickActionArgs, setIsLoading, setIsLoginModalOpen } = args
  const router = useRouter()
  const {
    actionName,
    addItemsToCart,
    navigate,
    revertUpsell,
    upsellToCart,
    addSelectedSKUsToCart,
    loginModal,
    analyticsModule,
  } = useClickActionArgs

  const { setIsLoadingAnyAction } = useClickActionContext()
  const { locale } = useLocale()
  const pdp = usePDP()
  const { setCart } = useCart()
  const localizedRouter = useLocalizedRouter()
  const errors = useErrors()
  const { sierra } = useSierra()
  const { setOpen: setModalOpen } = useModal()
  const openModal = args?.useClickActionArgs?.openModal
  const modalId = openModal?.modalId

  const { trackOpenModal } = useTrackOpenModal({
    openModal,
    analyticsModule,
  })
  const { trackNavigate } = useTrackNavigate({ navigate, analyticsModule })
  const { trackAction } = useTrackAction({ actionName, analyticsModule })

  const { slug: railSlug } = useRailItemContext()

  const { user } = useSession()

  const isLoggedIn = Boolean(user?.email)

  const selectSKU = useSKUSelectionStore((state) => state.selectSKU)
  const getSelectedSKUs = useSKUSelectionStore((state) => state.getSelectedSKUs)
  const selectSKUValue = args?.useClickActionArgs?.selectSKU?.sku

  const onClick = useCallback(async () => {
    if (shouldSkip) return

    if (actionName === 'navigate') {
      trackNavigate()
      return
    }
    if (actionName === 'popoutVideo') {
      setVideoOpen(true)
      return
    }
    if (actionName === 'openModal' && modalId) {
      setModalOpen(modalId)
      trackOpenModal()
      return
    }
    if (actionName === 'loginModal') {
      if (!isLoggedIn) {
        setIsLoginModalOpen(true)
        return
      }
      if (isLoggedIn && loginModal?.redirectUrl) {
        localizedRouter.push(loginModal.redirectUrl)
        return
      }
    }
    if (actionName === 'selectSKU' && railSlug && selectSKUValue) {
      selectSKU(railSlug, selectSKUValue)
      return
    }

    if (actionName === 'openChatbotWindow') {
      if (sierra) {
        sierra?.openChatModal()
      } else {
        log.warn('[Click Action] Sierra has not been initialized in this page.')
      }
      return
    }

    // server actions called below
    try {
      const redirectOrStopLoading = (url?: string) => {
        if (url === '/cart') {
          router.replace('#cart')
          setIsLoading(false)
          setIsLoadingAnyAction(false)
          return
        }
        if (url) {
          localizedRouter.push(url)
        } else {
          setIsLoading(false)
          setIsLoadingAnyAction(false)
        }
      }

      setIsLoading(true)
      setIsLoadingAnyAction(true)

      if (actionName === 'addPDPItemToCart') {
        await pdp.addToCart()
        redirectOrStopLoading()
        return
      }

      if (actionName === 'addItemsToCart') {
        if (configPublic.featureFlags.shopifyEnabled) {
          const addToCartUrl = new URL(`${getShopifyBaseUrl(locale.code)}/cart`)
          addToCartUrl.searchParams.set(
            'addItemsToCart',
            JSON.stringify({
              items: addItemsToCart?.skus?.map((item) => ({
                id: item.variantId,
                quantity: item.quantity ?? 1,
                selling_plan: item.subscriptionPlanId ?? '',
              })),
              redirectUrl: addItemsToCart?.shopifyRedirectUrl ?? '',
            })
          )
          // TODO: implement tracking for shopify add to cart
          window.location.href = addToCartUrl.toString()
        } else {
          const skus = addItemsToCart?.skus
          if (!skus) return
          const cart = await ServerActions.serial.addLineItems(skus)
          setCart(cart)
          await trackAction(useClickActionArgs, cart)
          redirectOrStopLoading(addItemsToCart?.redirectUrl)
        }
        return
      }

      if (actionName === 'upsellToCart') {
        const skus = upsellToCart?.skus
        if (!skus) return
        const cart = await ServerActions.serial.upsellLineItems(skus)

        await trackAction(useClickActionArgs, cart)
        redirectOrStopLoading(upsellToCart?.redirectUrl)
        return
      }

      if (actionName === 'revertUpsell') {
        const cart = await ServerActions.serial.revertUpsell()

        await trackAction(useClickActionArgs, cart)
        redirectOrStopLoading(revertUpsell?.redirectUrl)
      }

      if (actionName === 'addSelectedSKUsToCart') {
        const selectedSkus = getSelectedSKUs()
        const extraSkus = addSelectedSKUsToCart?.extraSkus?.map((val) => val.sku) ?? []
        const bundleId = addSelectedSKUsToCart?.bundleId
        const skus = [...selectedSkus, ...extraSkus].map((sku) => ({
          sku,
          quantity: 1,
          ...(bundleId
            ? {
                custom: {
                  bundleId,
                },
              }
            : {}),
        }))

        const SKUSelectionState = useSKUSelectionStore.getState()
        const isSKUSelectionValid = isSelectionStateValid(
          SKUSelectionState.groups,
          SKUSelectionState.groupRestrictions
        )

        if (!isSKUSelectionValid || !skus) {
          setIsLoading(false)
          setIsLoadingAnyAction(false)
          return
        }

        const cart = await ServerActions.serial.addLineItems(skus)
        setCart(cart)
        await trackAction(useClickActionArgs, cart)
        redirectOrStopLoading(addSelectedSKUsToCart?.redirectUrl)
        return
      }
    } catch (error: any) {
      setIsLoading(false)
      setIsLoadingAnyAction(false)
      log.error(`[Click Action] Error while performing action - ${error?.message}`, error)
      errors.setError(error, { group: BuilderActionErrorsGroup })
    }
  }, [
    shouldSkip,
    actionName,
    modalId,
    railSlug,
    selectSKUValue,
    trackNavigate,
    setVideoOpen,
    setModalOpen,
    trackOpenModal,
    isLoggedIn,
    loginModal?.redirectUrl,
    setIsLoginModalOpen,
    localizedRouter,
    selectSKU,
    sierra,
    setIsLoading,
    setIsLoadingAnyAction,
    addItemsToCart,
    router,
    pdp,
    locale.code,
    setCart,
    trackAction,
    useClickActionArgs,
    upsellToCart?.skus,
    upsellToCart?.redirectUrl,
    revertUpsell?.redirectUrl,
    getSelectedSKUs,
    addSelectedSKUsToCart?.extraSkus,
    addSelectedSKUsToCart?.bundleId,
    addSelectedSKUsToCart?.redirectUrl,
    errors,
  ])

  return { onClick }
}
